import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import LazyLoad from "@tripbuilder/image/LazyLoad";
import { cn } from "@heroui/theme";
import { CancelButton, ConfirmButton } from "@/components/common/buttons";

interface BackgroundSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBackgroundSelected: (backgroundUrl: string) => void;
  setIsBackgroundLoading: (isLoading: boolean) => void;
}

// Placeholder background images (replace with actual images later)
// Background options with descriptive names and optimized image parameters
const backgroundOptions = [
  "https://imagedelivery.net/-nMEN5vW69LWTqJjOcxQog/40495133-687f-48b8-bb71-658fb8422800/large",
  "https://imagedelivery.net/-nMEN5vW69LWTqJjOcxQog/48249c39-6bf6-4771-dc89-5a718d333700/large",
  "https://imagedelivery.net/-nMEN5vW69LWTqJjOcxQog/5418fbbe-340d-4210-bbdf-a340d6eebb00/large",
  "https://imagedelivery.net/-nMEN5vW69LWTqJjOcxQog/653cf44a-7f4a-4479-0237-7c381ccd2e00/large",
];

export const BackgroundSelectionModal: React.FC<
  BackgroundSelectionModalProps
> = ({ isOpen, onClose, onBackgroundSelected, setIsBackgroundLoading }) => {
  const [selectedBackground, setSelectedBackground] = useState<string | null>(
    null
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleBackgroundSelect = (backgroundUrl: string) => {
    setSelectedBackground(backgroundUrl);
    setHasChanges(true);
  };

  const handleApplyBackground = async () => {
    if (!selectedBackground) {
      toast({
        title: "No background selected",
        description: "Please select a background image first",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setIsBackgroundLoading(true);
      onBackgroundSelected(selectedBackground);
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update background image",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Select Background Image</DialogTitle>
          <p className="text-sm text-neutral-500 font-light">
            Pick a background image for your profile.
          </p>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          {backgroundOptions.map((background, index) => (
            <div
              key={index}
              className={`relative cursor-pointer rounded-lg overflow-hidden h-32 transition-all duration-200 ${
                selectedBackground === background
                  ? "ring-2 ring-offset-2 ring-indigo-600"
                  : "hover:opacity-90 hover:shadow-md"
              }`}
              onClick={() => handleBackgroundSelect(background)}
            >
              <LazyLoad
                src={background}
                alt={`Background ${index + 1}`}
                className="w-full h-full object-cover"
              />
              {selectedBackground === background && (
                <div className="absolute inset-0 bg-indigo-600/10 flex items-center justify-center">
                  <div className="bg-white rounded-full p-2">
                    <Check
                      className="w-4 h-4 text-indigo-600"
                      strokeWidth={3}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <DialogFooter className="py-4 border-t border-neutral-100">
          <CancelButton onClick={onClose} disabled={isLoading} />
          <ConfirmButton
            title="Save"
            onClick={handleApplyBackground}
            isLoading={isLoading}
            disabled={isLoading || !hasChanges}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
